// Gemeente KPI-dashboard. PRD-05 §3.1.

import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { MunicipalityLayout } from "@/components/MunicipalityLayout";

interface DashboardData {
  kpis: {
    activeClients: number;
    newClientsThisMonth: number;
    monthGrowthPct: number | null;
    counselors: number;
    onboardingInProgress: number;
    onboardingCompleted: number;
  };
  alerts: {
    pendingConceptReviews: number;
    highCriticalEmails: number;
  };
}

export function MunicipalityDashboardPage() {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ["municipality-dashboard"],
    queryFn: () => api.get<DashboardData>("/v1/municipality/dashboard"),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const errorKey =
    query.error instanceof ApiError ? query.error.i18nKey : "errors.internal";

  const data = query.data;

  return (
    <MunicipalityLayout title={t("municipality.dashboard.title", "Dashboard")}>
      {query.isLoading ? (
        <p className="text-sm text-neutral-500">{t("loading", "Laden…")}</p>
      ) : query.isError ? (
        <p className="text-sm text-red-700">{t(errorKey, "Onverwachte fout")}</p>
      ) : data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <ActiveClientsCard
            value={data.kpis.activeClients}
            growthPct={data.kpis.monthGrowthPct}
            newThisMonth={data.kpis.newClientsThisMonth}
            growthLabel={t("municipality.dashboard.monthGrowth", "Maand-groei")}
            label={t("municipality.dashboard.activeClients", "Actieve clienten")}
          />

          <CounselorsCard
            value={data.kpis.counselors}
            inProgress={data.kpis.onboardingInProgress}
            completed={data.kpis.onboardingCompleted}
            label={t("municipality.dashboard.counselors", "Counselors")}
            onboardingLabel={t("municipality.dashboard.onboarding", "Onboarding")}
          />

          <AlertCard
            value={data.alerts.pendingConceptReviews}
            label={t("municipality.dashboard.pendingReviews", "Concept-reviews open")}
            tone={data.alerts.pendingConceptReviews > 0 ? "red" : "neutral"}
          />

          <AlertCard
            value={data.alerts.highCriticalEmails}
            label={t("municipality.dashboard.highCriticalEmails", "High/Critical emails")}
            tone={data.alerts.highCriticalEmails > 0 ? "amber" : "neutral"}
          />
        </div>
      ) : null}
    </MunicipalityLayout>
  );
}

function ActiveClientsCard({
  value,
  growthPct,
  newThisMonth,
  label,
  growthLabel,
}: {
  value: number;
  growthPct: number | null;
  newThisMonth: number;
  label: string;
  growthLabel: string;
}) {
  const isUp = growthPct !== null && growthPct >= 0;
  return (
    <div className="px-4 py-3 rounded-xl border border-neutral-200 bg-white">
      <p className="text-xs text-neutral-600">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value}</p>
      <p className="text-xs text-neutral-500 mt-2">
        +{newThisMonth} {growthLabel}{" "}
        {growthPct !== null ? (
          <span className={isUp ? "text-green-700" : "text-red-700"}>
            {isUp ? "↑" : "↓"} {Math.abs(growthPct)}%
          </span>
        ) : (
          <span className="text-neutral-400">–</span>
        )}
      </p>
    </div>
  );
}

function CounselorsCard({
  value,
  inProgress,
  completed,
  label,
  onboardingLabel,
}: {
  value: number;
  inProgress: number;
  completed: number;
  label: string;
  onboardingLabel: string;
}) {
  const total = inProgress + completed;
  const completedPct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="px-4 py-3 rounded-xl border border-neutral-200 bg-white">
      <p className="text-xs text-neutral-600">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value}</p>
      <div className="mt-3">
        <div className="flex items-center justify-between text-[10px] text-neutral-500 mb-1">
          <span>{onboardingLabel}</span>
          <span>
            {completed}/{total}
          </span>
        </div>
        <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500"
            style={{ width: `${completedPct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function AlertCard({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone: "red" | "amber" | "neutral";
}) {
  const styles = {
    red: "bg-red-50 border-red-200 text-red-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    neutral: "bg-white border-neutral-200 text-neutral-900",
  };
  return (
    <div className={`px-4 py-3 rounded-xl border ${styles[tone]}`}>
      <p className="text-xs opacity-80">{label}</p>
      <div className="flex items-center gap-2 mt-1">
        <p className="text-2xl font-semibold">{value}</p>
        {value > 0 && tone === "red" && (
          <span className="px-1.5 py-0.5 rounded bg-red-200 text-red-900 text-[10px] font-semibold">
            !
          </span>
        )}
      </div>
    </div>
  );
}
